import { z } from 'zod';
import { isValidCnpj, sanitizeCnpj } from './cnpj';

// Paleta default alinhada ao design system (dark primary + light/complementos).
// 12 cores que funcionam em ambos os temas.
export const SERVICE_COLOR_PALETTE = [
  '#2dd4bf', // teal accent
  '#818cf8', // indigo accent-secondary
  '#f472b6', // pink
  '#fbbf24', // amber
  '#34d399', // emerald
  '#f87171', // red
  '#60a5fa', // sky
  '#a78bfa', // violet
  '#fb923c', // orange
  '#4ade80', // green
  '#22d3ee', // cyan
  '#e879f9', // fuchsia
] as const;

export const SERVICE_COLORS = SERVICE_COLOR_PALETTE;

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export const hexColorSchema = z
  .string()
  .trim()
  .refine((v) => HEX_RE.test(v), 'Cor inválida (use #RRGGBB)');

const moneySchema = z
  .union([z.string(), z.number()])
  .transform((v) => {
    if (typeof v === 'number') return v;
    const cleaned = v.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  })
  .refine((v) => !Number.isNaN(v) && v >= 0, 'Valor inválido');

export const examItemSchema = z.object({
  shortName: z.string().trim().min(1, 'Sigla obrigatória').max(20, 'Máx 20 caracteres'),
  name: z.string().trim().min(1, 'Nome obrigatório').max(120, 'Máx 120 caracteres'),
  unitValue: moneySchema,
});
export type ExamItemInput = z.infer<typeof examItemSchema>;

// Schema "completo" usado na tela /services. Mantém compatível com o schema
// simples do onboarding (mesmos campos nucleares) mas adiciona campos fiscais
// opcionais e aceita hex livre (não só da paleta).
export const serviceSchema = z
  .object({
    name: z.string().trim().min(2, 'Informe o nome').max(120, 'Máx 120 caracteres'),
    color: hexColorSchema,
    fixedShiftValue: moneySchema,
    // Fiscal (opcionais — vazio = null no backend)
    tomadorCnpj: z
      .string()
      .trim()
      .optional()
      .transform((v) => (v ? sanitizeCnpj(v) : ''))
      .refine((v) => v === '' || v.length === 14, 'CNPJ deve ter 14 dígitos')
      .refine((v) => v === '' || isValidCnpj(v), 'CNPJ inválido'),
    tomadorRazaoSocial: z.string().trim().max(200).optional().default(''),
    tomadorEmail: z
      .string()
      .trim()
      .optional()
      .refine(
        (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
        'E-mail inválido',
      ),
    serviceCode: z.string().trim().max(20).optional().default(''),
    issRetained: z.boolean().default(false),
    issAliquot: z
      .union([z.string(), z.number()])
      .optional()
      .transform((v) => {
        if (v === undefined || v === '' || v === null) return undefined;
        return typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
      })
      .refine(
        (v) => v === undefined || (Number.isFinite(v) && v >= 0 && v <= 100),
        'Alíquota inválida (0–100)',
      ),
    // Somente no fluxo de criação — editar exames é fluxo à parte.
    examTypes: z.array(examItemSchema).default([]),
  })
  .refine(
    (v) => {
      // Se informou qualquer dado de tomador, exige CNPJ + razão social (coerência fiscal).
      const anyTomador = v.tomadorRazaoSocial || v.tomadorEmail;
      if (anyTomador && !v.tomadorCnpj) return false;
      return true;
    },
    { message: 'Informe o CNPJ do tomador ao preencher os demais campos fiscais', path: ['tomadorCnpj'] },
  );

export type ServiceInput = z.infer<typeof serviceSchema>;

// Helpers de conversão com o backend (backend usa null; formulário usa string vazia).
export function serviceInputToApiPayload(
  values: ServiceInput,
  cnpjId: string,
): Record<string, unknown> {
  return {
    cnpjId,
    name: values.name,
    color: values.color,
    fixedShiftValue: values.fixedShiftValue,
    tomadorCnpj: values.tomadorCnpj || null,
    tomadorRazaoSocial: values.tomadorRazaoSocial || null,
    tomadorEmail: values.tomadorEmail || null,
    serviceCode: values.serviceCode || null,
    issRetained: values.issRetained ?? false,
    issAliquot: values.issAliquot ?? null,
  };
}

// Perfil do médico (usado no onboarding — preservado para retrocompatibilidade).
export const profileSchema = z.object({
  name: z.string().trim().min(2, 'Informe seu nome'),
  crm: z
    .string()
    .trim()
    .regex(/^\d{4,7}\/[A-Z]{2}$/, 'CRM no formato 12345/UF'),
  specialty: z.string().trim().optional(),
  phone: z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => v.length === 10 || v.length === 11, 'Telefone inválido'),
});
export type ProfileInput = z.infer<typeof profileSchema>;
